import type { OnboardingProfileDto } from '@shared/dto/onboarding/onboarding-profile.dto';
import type { MunicipalityResult } from '../../services/municipality/municipality.service.interface';
import { getHttpErrorStatus } from '../../utils/error.utils';
import type { OnboardingFormDto } from './dto/onboarding-form.dto';

export const ONBOARDING_STORAGE_KEY = 'prospero_onboarding_data';

/**
 * Query key for the profile-seed fetch used by the onboarding wizard. Shared with
 * <BootstrapError /> so its retry button can re-fetch a failed profile (the query
 * uses `staleTime: Infinity`, so it only refetches when explicitly invalidated).
 */
export const ONBOARDING_PROFILE_SEED_QUERY_KEY = ['auth', 'profile', 'onboarding-seed'] as const;

/**
 * Decides whether the onboarding wizard must replace itself with the retry screen
 * because the profile-seed bootstrap call failed. A 401 is excluded on purpose: a
 * rejected token is handled globally by the ky signOut hook (session expired →
 * redirect to sign-in), so it must not be turned into a retry screen.
 */
export function shouldSurfaceProfileBootstrapError(isError: boolean, error: unknown): boolean {
  return isError && getHttpErrorStatus(error) !== 401;
}

export type OnboardingGate = 'loading' | 'login' | 'resolve-tenant' | 'error' | 'home' | 'form';

/**
 * Gate for the onboarding route, which sits OUTSIDE `ProtectedRoute` and so has
 * to re-derive its own bootstrap state. It mirrors `isBootstrappingAuth`: the
 * status query is tenant-scoped, and while it is disabled TanStack reports
 * `isLoading:false` with `data:undefined` — gating on that mounted the wizard
 * with an empty status for a frame (the flash users reported on a cold landing).
 * Consult `statusPending` only once the query can actually run.
 */
export function resolveOnboardingGate(input: {
  isLoaded: boolean;
  isSignedIn: boolean;
  membershipsPending: boolean;
  hasValidatedTenant: boolean;
  statusPending: boolean;
  statusError: boolean;
  onboardingCompleted: boolean | undefined;
}): OnboardingGate {
  if (!input.isLoaded) {
    return 'loading';
  }
  if (!input.isSignedIn) {
    return 'login';
  }
  if (input.membershipsPending) {
    return 'loading';
  }
  if (!input.hasValidatedTenant) {
    return 'resolve-tenant';
  }
  if (input.statusPending) {
    return 'loading';
  }
  if (input.statusError) {
    return 'error';
  }
  return input.onboardingCompleted ? 'home' : 'form';
}

/**
 * Draft persisted in localStorage. Drafts written by the old two-step wizard may
 * still carry `activeStep`, `billingInterval`, `couponId`, `discountCode` and
 * `couponInfo`: they are tolerated and ignored on read.
 */
export interface SavedOnboardingData {
  values: Partial<OnboardingFormDto>;
  activeStep: number;
  cityInputValue: string;
  tenantId: string;
}

export function getCurrentTenantId(): string | null {
  try {
    const tenantContext = localStorage.getItem('tenant_context');
    if (tenantContext) {
      const parsed = JSON.parse(tenantContext) as { currentTenantId?: string };
      return parsed.currentTenantId || null;
    }
  } catch {
    // ignore
  }
  return null;
}

export function clearSavedOnboardingData(): void {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function loadSavedOnboardingData(): SavedOnboardingData | null {
  try {
    const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved) as SavedOnboardingData;
      const currentTenantId = getCurrentTenantId();
      if (data.tenantId && currentTenantId && data.tenantId !== currentTenantId) {
        clearSavedOnboardingData();
        return null;
      }
      return data;
    }
  } catch {
    // ignore parse errors
  }
  return null;
}

/**
 * Registry payload of the single-step wizard (`POST onboarding/complete`). The
 * optional mobile is dropped when blank: '' would not skip the API's @IsMobile
 * check.
 */
export function toProfilePayload(values: OnboardingFormDto): OnboardingProfileDto {
  const phone = values.phone?.trim() ? values.phone : undefined;
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    ragioneSociale: values.ragioneSociale,
    partitaIva: values.partitaIva,
    indirizzoVia: values.indirizzoVia,
    indirizzoCap: values.indirizzoCap,
    indirizzoCitta: values.indirizzoCitta,
    indirizzoProvincia: values.indirizzoProvincia,
    codiceSdi: values.codiceSdi,
    pec: values.pec,
    ...(phone ? { phone } : {}),
  };
}

interface ProfileSeedValues {
  firstName?: string;
  lastName?: string | null;
}

function getProfileValuesFromSaved(
  values: Partial<OnboardingFormDto> | undefined,
  seed?: ProfileSeedValues,
): Pick<
  OnboardingFormDto,
  | 'firstName'
  | 'lastName'
  | 'ragioneSociale'
  | 'partitaIva'
  | 'phone'
  | 'indirizzoVia'
  | 'indirizzoCap'
  | 'indirizzoCitta'
  | 'indirizzoProvincia'
  | 'codiceSdi'
  | 'pec'
> {
  return {
    firstName: values?.firstName ?? seed?.firstName ?? '',
    lastName: values?.lastName ?? seed?.lastName ?? '',
    ragioneSociale: values?.ragioneSociale ?? '',
    partitaIva: values?.partitaIva ?? '',
    phone: values?.phone ?? '',
    indirizzoVia: values?.indirizzoVia ?? '',
    indirizzoCap: values?.indirizzoCap ?? '',
    indirizzoCitta: values?.indirizzoCitta ?? '',
    indirizzoProvincia: values?.indirizzoProvincia ?? '',
    codiceSdi: values?.codiceSdi ?? '',
    pec: values?.pec ?? '',
  };
}

/**
 * Fields the municipality pick writes on the user's behalf, so the form can
 * point at them: the province always, the CAP only when it is unambiguous.
 */
export function resolveSystemFilledFields(
  municipality: Pick<MunicipalityResult, 'cap'> | null,
): Array<keyof OnboardingFormDto> {
  if (!municipality) {
    return [];
  }
  return municipality.cap.length === 1
    ? ['indirizzoProvincia', 'indirizzoCap']
    : ['indirizzoProvincia'];
}

const COMPLETION_HOLD_MS = 1100;
const COMPLETION_HOLD_REDUCED_MOTION_MS = 600;

/**
 * How long the "workspace ready" screen stays before the status refresh sends
 * the user home. Reduced motion has nothing to play, but the outcome still
 * needs a beat to be read.
 */
export function resolveCompletionHoldMs(prefersReducedMotion: boolean): number {
  return prefersReducedMotion ? COMPLETION_HOLD_REDUCED_MOTION_MS : COMPLETION_HOLD_MS;
}

export function getInitialOnboardingFormValues(
  savedData: SavedOnboardingData | null,
  profileSeed?: ProfileSeedValues,
): OnboardingFormDto {
  const values = savedData?.values;

  return {
    ...getProfileValuesFromSaved(values, profileSeed),
    tosAccepted: values?.tosAccepted ?? false,
    unfairTermsAccepted: values?.unfairTermsAccepted ?? false,
  };
}
